import { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Shield,
  Flame,
  Timer,
  Calculator,
  Lock,
  Plus,
  Minus,
  DollarSign,
} from 'lucide-react';
import { useLocale } from '../../contexts/LocaleContext';
import Navbar from '../../components/ALLJsx/Navbar';
import Footer from '../../components/ALLJsx/Footer';
import BrandLogo from '../../components/ALLJsx/BrandLogo';
import { US_STATES } from '../../data/usStates';
import { API_URL } from '../../services/api';
import PrequalTermsContent from './PrequalTermsContent';
import '../AllCss/FinanceApply.css';

const AVERAGE_CAR_PRICE = 26350;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function digitsOnly(s) {
  return String(s ?? '').replace(/\D/g, '');
}

function sanitizePhoneDigits(v) {
  return digitsOnly(v).slice(0, 10);
}

/** US display: (555) 123-4567 — state stores digits only */
function formatUsPhone(v) {
  const x = sanitizePhoneDigits(v);
  if (!x.length) return '';
  if (x.length <= 3) return `(${x}`;
  if (x.length <= 6) return `(${x.slice(0, 3)}) ${x.slice(3)}`;
  return `(${x.slice(0, 3)}) ${x.slice(3, 6)}-${x.slice(6)}`;
}

function sanitizeZipDigits(v) {
  return digitsOnly(v).slice(0, 9);
}

/** US display: 12345 or 12345-6789 — state stores digits only */
function formatUsZip(v) {
  const x = sanitizeZipDigits(v);
  if (!x.length) return '';
  if (x.length <= 5) return x;
  return `${x.slice(0, 5)}-${x.slice(5)}`;
}

/** Readonly money from calculations — 9,999.99 style (always 2 decimals, comma ≥ 1,000) */
function formatMoneyFigures(n) {
  if (n == null || !Number.isFinite(Number(n))) return '0.00';
  return Number(n).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Money fields store digits only (maxLen), strict bank style: 1 -> 0.01, 12 -> 0.12, 123 -> 1.23 */
function normalizeMoneyIntInput(raw, maxLen) {
  return digitsOnly(raw).slice(0, maxLen);
}

function formatMoneyIntDisplay(stored) {
  if (stored === '' || stored == null) return '';
  const d = String(stored).replace(/\D/g, '');
  if (!d) return '';
  const normalized = d.padStart(3, '0');
  const dollars = normalized.slice(0, -2);
  const cents = normalized.slice(-2);
  const amount = parseFloat(`${dollars}.${cents}`);
  if (!Number.isFinite(amount)) return '';
  return amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Map stored digit string to a dollar amount for validation and API payload */
function parseMoneyDigitsToNumber(stored) {
  if (stored === '' || stored == null) return NaN;
  const d = String(stored).replace(/\D/g, '');
  if (!d) return NaN;
  const normalized = d.padStart(3, '0');
  return parseFloat(`${normalized.slice(0, -2)}.${normalized.slice(-2)}`);
}

function normalizeHoursPerWeekInput(raw) {
  const d = digitsOnly(raw).slice(0, 3);
  if (!d) return '';
  const n = parseInt(d, 10);
  if (!Number.isFinite(n)) return '';
  if (n > 168) return '168';
  return String(n);
}

const emptyForm = () => ({
  storeId: '',
  storeName: '',
  desiredPrice: '',
  downPayment: '',
  firstName: '',
  lastName: '',
  dob: '',
  email: '',
  phone: '',
  ssn: '',
  coFirstName: '',
  coLastName: '',
  coDob: '',
  coEmail: '',
  coPhone: '',
  coSsn: '',
  street: '',
  apt: '',
  city: '',
  state: 'AL',
  zip: '',
  housingStatus: '',
  moveInDate: '',
  monthlyHousing: '',
  employmentStatus: '',
  employerName: '',
  jobTitle: '',
  employmentStartDate: '',
  incomeType: 'salary',
  hourlyRate: '',
  hoursPerWeek: '',
  annualIncome: '',
  monthlyIncome: '',
  additionalIncome: '',
});

/** Public URL: /get-financed — single-page pre-qualification form */
function FinanceApply() {
  const { t } = useLocale();
  const [form, setForm] = useState(emptyForm);
  const [stores, setStores] = useState([]);
  const [storesLoading, setStoresLoading] = useState(true);
  const [storesFetchFailed, setStoresFetchFailed] = useState(false);
  const [storeDropdownOpen, setStoreDropdownOpen] = useState(false);
  const [storeDropdownPos, setStoreDropdownPos] = useState(null);
  const storeTriggerRef = useRef(null);
  const storeDropdownPortalRef = useRef(null);
  const [showCoBuyer, setShowCoBuyer] = useState(false);
  const [showAdditionalIncome, setShowAdditionalIncome] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const pq = useCallback((key, opts) => t(`finance.prequal.${key}`, opts), [t]);
  const requiredFieldError = (fieldLabel) => `Please fill in your ${fieldLabel}.`;

  const incomeResolved = useMemo(() => {
    const type = form.incomeType || 'salary';
    if (type === 'salary') {
      const annualNum = parseMoneyDigitsToNumber(form.annualIncome);
      if (!Number.isFinite(annualNum) || annualNum <= 0) return null;
      const monthly = Math.round((annualNum / 12) * 100) / 100;
      return { annual: annualNum, monthly };
    }
    if (type === 'hourly') {
      const rate = parseMoneyDigitsToNumber(form.hourlyRate);
      const hrs = Number(form.hoursPerWeek);
      if (!Number.isFinite(rate) || rate <= 0 || !Number.isFinite(hrs) || hrs <= 0) return null;
      const monthly = Math.round(((rate * hrs * 52) / 12) * 100) / 100;
      const annual = Math.round(monthly * 12 * 100) / 100;
      return { annual, monthly };
    }
    const annualNum = parseMoneyDigitsToNumber(form.annualIncome);
    const monthlyNum = parseMoneyDigitsToNumber(form.monthlyIncome);
    if (!Number.isFinite(annualNum) || annualNum <= 0 || !Number.isFinite(monthlyNum) || monthlyNum <= 0)
      return null;
    return { annual: annualNum, monthly: monthlyNum };
  }, [form.incomeType, form.annualIncome, form.monthlyIncome, form.hourlyRate, form.hoursPerWeek]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setStoresLoading(true);
      setStoresFetchFailed(false);
      const zip = (localStorage.getItem('selectedZipcode') || '10001').trim();
      let list = null;

      try {
        const { data } = await axios.get(`${API_URL}/api/shipping/stores`);
        if (
          !cancelled &&
          data?.success &&
          Array.isArray(data.data?.stores) &&
          data.data.stores.length > 0
        ) {
          list = data.data.stores;
        }
      } catch {
        /* use fallback below */
      }

      if (!list) {
        try {
          const { data } = await axios.get(`${API_URL}/api/shipping/store-by-zip`, {
        withCredentials: true,
            params: { zip },
          });
          if (
            !cancelled &&
            data?.success &&
            Array.isArray(data.data?.stores) &&
            data.data.stores.length > 0
          ) {
            list = data.data.stores;
          }
        } catch {
          /* failed */
        }
      }

      if (!cancelled) {
        if (list?.length) {
          setStores(list);
          setStoresFetchFailed(false);
        } else {
          setStores([]);
          setStoresFetchFailed(true);
        }
        setStoresLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (stores.length === 0) return;
    setForm((f) => {
      if (f.storeId) return f;
      const zip = (localStorage.getItem('selectedZipcode') || '').trim();
      const match = zip ? stores.find((s) => String(s.zipCode) === zip) : null;
      const pick = match || stores[0];
      return { ...f, storeId: String(pick.id), storeName: pick.name };
    });
  }, [stores]);

  const updateStoreDropdownPosition = useCallback(() => {
    const el = storeTriggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setStoreDropdownPos({
      top: r.bottom + 4,
      left: r.left,
      width: Math.max(r.width, 280),
    });
  }, []);

  useLayoutEffect(() => {
    if (!storeDropdownOpen) {
      setStoreDropdownPos(null);
      return;
    }
    updateStoreDropdownPosition();
    window.addEventListener('scroll', updateStoreDropdownPosition, true);
    window.addEventListener('resize', updateStoreDropdownPosition);
    return () => {
      window.removeEventListener('scroll', updateStoreDropdownPosition, true);
      window.removeEventListener('resize', updateStoreDropdownPosition);
    };
  }, [storeDropdownOpen, updateStoreDropdownPosition]);

  useEffect(() => {
    if (!storeDropdownOpen) return;
    const onDoc = (e) => {
      const t = e.target;
      if (storeTriggerRef.current?.contains(t)) return;
      if (storeDropdownPortalRef.current?.contains(t)) return;
      setStoreDropdownOpen(false);
    };
    const id = window.setTimeout(() => {
      document.addEventListener('mousedown', onDoc);
    }, 0);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener('mousedown', onDoc);
    };
  }, [storeDropdownOpen]);

  useEffect(() => {
    if (!storeDropdownOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setStoreDropdownOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [storeDropdownOpen]);

  const selectedStore = useMemo(
    () => stores.find((s) => String(s.id) === String(form.storeId)),
    [stores, form.storeId]
  );

  const update = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (error) setError('');
  };

  const removeCoBuyer = () => {
    setShowCoBuyer(false);
    setForm((prev) => ({
      ...prev,
      coFirstName: '',
      coLastName: '',
      coDob: '',
      coEmail: '',
      coPhone: '',
      coSsn: '',
    }));
    if (error) setError('');
  };

  const validateForm = () => {
    if (storesLoading) {
      setError(pq('storesLoading'));
      return false;
    }
    if (storesFetchFailed) {
      setError(pq('storesFetchError'));
      return false;
    }
    if (stores.length === 0) {
      setError(pq('noStoresConfigured'));
      return false;
    }
    if (!form.storeId) {
      setError(pq('selectStoreError'));
      return false;
    }
    const price = parseMoneyDigitsToNumber(form.desiredPrice);
    if (!form.storeName?.trim()) {
      setError(requiredFieldError('store location'));
      return false;
    }
    if (!Number.isFinite(price) || price <= 0) {
      setError(requiredFieldError('desired car price'));
      return false;
    }
    if (!form.firstName.trim()) {
      setError(requiredFieldError('first name'));
      return false;
    }
    if (!form.lastName.trim()) {
      setError(requiredFieldError('last name'));
      return false;
    }
    if (!form.dob) {
      setError(requiredFieldError('date of birth'));
      return false;
    }
    if (!form.email?.trim()) {
      setError(requiredFieldError('email'));
      return false;
    }
    if (!EMAIL_RE.test(form.email.trim())) {
      setError(pq('invalidEmail'));
      return false;
    }
    if (!form.phone?.trim()) {
      setError(requiredFieldError('phone number'));
      return false;
    }
    const ph = digitsOnly(form.phone);
    if (ph.length !== 10) {
      setError(pq('invalidPhone'));
      return false;
    }
    if (!form.ssn?.trim()) {
      setError(requiredFieldError('Social Security number'));
      return false;
    }
    const ss = digitsOnly(form.ssn);
    if (ss.length !== 9) {
      setError(pq('invalidSsn'));
      return false;
    }
    if (showCoBuyer) {
      if (!form.coFirstName.trim()) {
        setError(requiredFieldError("co-buyer's first name"));
        return false;
      }
      if (!form.coLastName.trim()) {
        setError(requiredFieldError("co-buyer's last name"));
        return false;
      }
      if (!form.coDob) {
        setError(requiredFieldError("co-buyer's date of birth"));
        return false;
      }
      if (!form.coEmail?.trim()) {
        setError(requiredFieldError("co-buyer's email"));
        return false;
      }
      if (!EMAIL_RE.test(form.coEmail.trim())) {
        setError(pq('invalidEmail'));
        return false;
      }
      if (!form.coPhone?.trim()) {
        setError(requiredFieldError("co-buyer's phone number"));
        return false;
      }
      const coPh = digitsOnly(form.coPhone);
      if (coPh.length !== 10) {
        setError(pq('invalidPhone'));
        return false;
      }
      if (!form.coSsn?.trim()) {
        setError(requiredFieldError("co-buyer's Social Security number"));
        return false;
      }
      const coSs = digitsOnly(form.coSsn);
      if (coSs.length !== 9) {
        setError(pq('invalidSsn'));
        return false;
      }
    }
    const z = digitsOnly(form.zip);
    const housingNum = parseMoneyDigitsToNumber(form.monthlyHousing);
    const monthlyOk =
      form.monthlyHousing !== '' &&
      form.monthlyHousing !== undefined &&
      Number.isFinite(housingNum) &&
      !Number.isNaN(housingNum);
    if (!form.street.trim()) {
      setError(requiredFieldError('street address'));
      return false;
    }
    if (!form.city.trim()) {
      setError(requiredFieldError('city'));
      return false;
    }
    if (!form.state) {
      setError(requiredFieldError('state'));
      return false;
    }
    if (z.length < 5) {
      setError(requiredFieldError('ZIP code'));
      return false;
    }
    if (!form.housingStatus) {
      setError(requiredFieldError('housing status'));
      return false;
    }
    if (!form.moveInDate) {
      setError(requiredFieldError('move-in date'));
      return false;
    }
    if (!monthlyOk) {
      setError(requiredFieldError('monthly payment'));
      return false;
    }
    if (!form.employmentStatus) {
      setError(requiredFieldError('employment status'));
      return false;
    }
    const needsJobDetails =
      form.employmentStatus === 'employed' || form.employmentStatus === 'self';
    if (needsJobDetails) {
      if (!form.employerName?.trim()) {
        setError(requiredFieldError('employer name'));
        return false;
      }
      if (!form.jobTitle?.trim()) {
        setError(requiredFieldError('job title'));
        return false;
      }
      if (!form.employmentStartDate?.trim()) {
        setError(requiredFieldError('employment start date'));
        return false;
      }
    }
    if (!incomeResolved) {
      setError(requiredFieldError('income information'));
      return false;
    }
    if (!agreedToTerms) {
      setError(pq('termsRequired'));
      return false;
    }
    return true;
  };

  const buildSubmitPayload = (inc) => {
    const down = form.downPayment === '' ? null : parseMoneyDigitsToNumber(form.downPayment);
    const addInc =
      showAdditionalIncome && form.additionalIncome !== ''
        ? parseMoneyDigitsToNumber(form.additionalIncome)
        : null;
    return {
      acceptedTermsAndEsign: true,
      officeId: form.storeId?.trim() ? form.storeId.trim() : null,
      storeName: form.storeName?.trim() || null,
      desiredPrice: parseMoneyDigitsToNumber(form.desiredPrice),
      downPayment: Number.isFinite(down) && down >= 0 ? down : null,
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      dob: form.dob,
      email: form.email.trim(),
      phone: digitsOnly(form.phone),
      ssn: digitsOnly(form.ssn),
      street: form.street.trim(),
      apt: form.apt.trim() || null,
      city: form.city.trim(),
      state: form.state,
      zip: digitsOnly(form.zip),
      housingStatus: form.housingStatus,
      moveInDate: form.moveInDate,
      monthlyHousing: parseMoneyDigitsToNumber(form.monthlyHousing),
      employmentStatus: form.employmentStatus,
      employerName: form.employerName?.trim() || null,
      jobTitle: form.jobTitle?.trim() || null,
      employmentStartDate: form.employmentStartDate || null,
      incomeType: form.incomeType,
      hourlyRate: form.incomeType === 'hourly' ? parseMoneyDigitsToNumber(form.hourlyRate) : null,
      hoursPerWeek: form.incomeType === 'hourly' ? Number(form.hoursPerWeek) : null,
      annualIncome: inc.annual,
      monthlyIncome: inc.monthly,
      additionalIncome: addInc != null && addInc > 0 ? addInc : null,
      hasAdditionalIncome: Boolean(showAdditionalIncome && addInc != null && addInc > 0),
      coBuyer: showCoBuyer
        ? {
            firstName: form.coFirstName.trim(),
            lastName: form.coLastName.trim(),
            dob: form.coDob,
            email: form.coEmail.trim(),
            phone: digitsOnly(form.coPhone),
            ssn: digitsOnly(form.coSsn),
          }
        : null,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    if (!incomeResolved) return;
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/finance-applications`, buildSubmitPayload(incomeResolved));
      setSubmitted(true);
    } catch {
      setError(pq('submitError'));
    } finally {
      setSubmitting(false);
    }
  };

  const displayStore =
    selectedStore?.name || form.storeName?.trim() || pq('dealershipName');

  const selectStore = (s) => {
    if (!s) return;
    setForm((f) => ({ ...f, storeId: String(s.id), storeName: s.name || '' }));
    setStoreDropdownOpen(false);
    if (error) setError('');
  };

  if (submitted) {
    return (
      <div className="finance-apply-page">
        <Navbar />
        <main className="finance-apply-main finance-apply-main--narrow">
          <div className="finance-apply-success">
            <div className="finance-apply-success-icon" aria-hidden>
              <Shield size={28} strokeWidth={2} />
            </div>
            <h1 className="finance-apply-title">{pq('successTitle')}</h1>
            <p className="finance-apply-lead finance-apply-lead--success">{pq('successBody')}</p>
            <div className="finance-apply-success-actions">
              <Link to="/inventory" className="finance-apply-btn finance-apply-btn--primary">
                {t('home.browseInventory')}
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="finance-apply-page">
      <Navbar />
      <main className="finance-apply-main finance-apply-main--single">
        <header className="finance-apply-hero">
          <Link to="/" className="finance-apply-hero-brand" aria-label={pq('dealershipName')}>
            <BrandLogo className="finance-apply-hero-logo" alt="" />
          </Link>
          <h1 className="finance-apply-hero-title">{pq('heroTitle')}</h1>
          <ul className="finance-apply-benefits">
            <li>
              <Shield className="finance-apply-benefit-icon" size={22} aria-hidden />
              <span>{pq('benefit1')}</span>
            </li>
            <li>
              <Flame className="finance-apply-benefit-icon" size={22} aria-hidden />
              <span>{pq('benefit2')}</span>
            </li>
            <li>
              <Timer className="finance-apply-benefit-icon" size={22} aria-hidden />
              <span>{pq('benefit3')}</span>
            </li>
          </ul>
        </header>

        {error ? (
          <div className="finance-apply-error" role="alert">
            {error}
          </div>
        ) : null}

        <form className="finance-apply-card" onSubmit={handleSubmit} noValidate>
          <section className="finance-apply-section-block" aria-labelledby="pq-section-basics">
            <h2 id="pq-section-basics" className="finance-apply-section-title">
              {pq('sectionBasics')}
            </h2>

            <div className="finance-apply-store-picker">
              <div
                ref={storeTriggerRef}
                className="finance-apply-field finance-apply-field--row finance-apply-store-picker-row"
              >
                <div className="finance-apply-store-line-wrap">
                  <p className="finance-apply-store-line">
                    {pq('shoppingAtPrefix')}{' '}
                    <strong>{storesLoading ? pq('storesLoading') : displayStore}</strong>
                  </p>
                  {!storesLoading && selectedStore?.fullAddress ? (
                    <p className="finance-apply-store-subline">{selectedStore.fullAddress}</p>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="finance-apply-change-store"
                  onClick={() => stores.length > 0 && setStoreDropdownOpen((o) => !o)}
                  disabled={storesLoading || stores.length === 0}
                  aria-expanded={storeDropdownOpen}
                  aria-haspopup="listbox"
                >
                  {pq('changeStore')}
                </button>
              </div>
              {storeDropdownOpen && stores.length > 0 && storeDropdownPos
                ? createPortal(
                    <ul
                      ref={storeDropdownPortalRef}
                      className="finance-apply-store-dropdown finance-apply-store-dropdown--portal"
                      style={{
                        top: storeDropdownPos.top,
                        left: storeDropdownPos.left,
                        width: storeDropdownPos.width,
                      }}
                      role="listbox"
                      aria-label={pq('changeStore')}
                    >
                      {stores.map((s) => {
                        const isSelected = String(form.storeId) === String(s.id);
                        return (
                          <li key={s.id} role="presentation">
                            <button
                              type="button"
                              className={`finance-apply-store-option${isSelected ? ' is-selected' : ''}`}
                              role="option"
                              aria-selected={isSelected}
                              onClick={() => selectStore(s)}
                            >
                              <span className="finance-apply-store-option-name">{s.name}</span>
                              <span className="finance-apply-store-option-meta">
                                {[s.city, s.state, s.zipCode].filter(Boolean).join(', ')}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>,
                    document.body,
                  )
                : null}
            </div>

            <label className="finance-apply-field-label" htmlFor="pq-price">
              {pq('desiredCarPrice')}
            </label>
            <div className="finance-apply-input-icon-wrap finance-apply-input-icon-wrap--dollar">
              <DollarSign size={18} strokeWidth={2} className="finance-apply-currency-prefix" aria-hidden />
              <input
                id="pq-price"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                className="finance-apply-input finance-apply-input--currency finance-apply-input--money"
                placeholder="0.00"
                maxLength={13}
                value={formatMoneyIntDisplay(form.desiredPrice)}
                onChange={(e) => update('desiredPrice', normalizeMoneyIntInput(e.target.value, 9))}
              />
              <Calculator className="finance-apply-input-trail-icon" size={22} aria-hidden />
            </div>
            <p className="finance-apply-hint">
              {pq('avgCarPriceHint', { amount: `$${formatMoneyFigures(AVERAGE_CAR_PRICE)}` })}
            </p>

            <label className="finance-apply-field-label" htmlFor="pq-down">
              {pq('downPayment')}
            </label>
            <div className="finance-apply-currency-wrap">
              <DollarSign size={18} strokeWidth={2} className="finance-apply-currency-prefix" aria-hidden />
              <input
                id="pq-down"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                className="finance-apply-input finance-apply-input--currency finance-apply-input--money"
                placeholder="0.00"
                maxLength={13}
                value={formatMoneyIntDisplay(form.downPayment)}
                onChange={(e) => update('downPayment', normalizeMoneyIntInput(e.target.value, 9))}
              />
            </div>
          </section>

          <section className="finance-apply-section-block finance-apply-section-block--divider" aria-labelledby="pq-section-identity">
            <h2 id="pq-section-identity" className="finance-apply-section-title">
              {pq('sectionIdentity')}
            </h2>
            <div className="finance-apply-row2">
              <div>
                <label className="finance-apply-field-label" htmlFor="pq-fn">
                  {pq('firstName')}
                </label>
                <input
                  id="pq-fn"
                  className="finance-apply-input"
                  autoComplete="given-name"
                  placeholder={pq('firstName')}
                  value={form.firstName}
                  onChange={(e) => update('firstName', e.target.value)}
                />
              </div>
              <div>
                <label className="finance-apply-field-label" htmlFor="pq-ln">
                  {pq('lastName')}
                </label>
                <input
                  id="pq-ln"
                  className="finance-apply-input"
                  autoComplete="family-name"
                  placeholder={pq('lastName')}
                  value={form.lastName}
                  onChange={(e) => update('lastName', e.target.value)}
                />
              </div>
            </div>

            <label className="finance-apply-field-label" htmlFor="pq-dob">
              {pq('dateOfBirth')}
            </label>
            <input
              id="pq-dob"
              type="date"
              className="finance-apply-input"
              value={form.dob}
              onChange={(e) => update('dob', e.target.value)}
            />

            <label className="finance-apply-field-label" htmlFor="pq-email">
              {pq('email')}
            </label>
            <input
              id="pq-email"
              type="email"
              className="finance-apply-input"
              autoComplete="email"
              inputMode="email"
              placeholder={pq('email')}
              value={form.email}
              onChange={(e) => update('email', e.target.value)}
            />

            <label className="finance-apply-field-label" htmlFor="pq-phone">
              {pq('phone')}
            </label>
            <input
              id="pq-phone"
              type="tel"
              className="finance-apply-input"
              autoComplete="tel"
              inputMode="numeric"
              placeholder={pq('phonePlaceholder')}
              maxLength={14}
              value={formatUsPhone(form.phone)}
              onChange={(e) => update('phone', sanitizePhoneDigits(e.target.value))}
            />
            <p className="finance-apply-disclaimer">
              {pq('phoneDisclaimerStart')}{' '}
              <Link to="/terms-of-service" className="finance-apply-inline-link">
                {pq('textingTerms')}
              </Link>
              .
            </p>

            <div className="finance-apply-ssn-field">
              <label className="finance-apply-ssn-label" htmlFor="pq-ssn">
                {pq('ssn')}
              </label>
              <div className="finance-apply-ssn-inner">
                <Lock size={18} className="finance-apply-ssn-lock" aria-hidden />
                <input
                  id="pq-ssn"
                  type="password"
                  className="finance-apply-input finance-apply-input--ssn"
                  autoComplete="off"
                  inputMode="numeric"
                  maxLength={11}
                  placeholder="•••-••-••••"
                  value={form.ssn}
                  onChange={(e) => update('ssn', e.target.value)}
                />
              </div>
            </div>
            <p className="finance-apply-disclaimer">
              {pq('ssnDisclaimerStart')}{' '}
              <Link to="/privacy-policy" className="finance-apply-inline-link">
                {pq('privacyPolicy')}
              </Link>
              .
            </p>

            {!showCoBuyer ? (
              <button
                type="button"
                className="finance-apply-text-btn"
                onClick={() => setShowCoBuyer(true)}
              >
                <Plus size={18} className="finance-apply-text-btn-icon" aria-hidden />
                {pq('addCoBuyer')}
              </button>
            ) : (
              <div className="finance-apply-cobuyer">
                <div className="finance-apply-cobuyer-header">
                  <button
                    type="button"
                    className="finance-apply-remove-cobuyer"
                    onClick={removeCoBuyer}
                    aria-label={pq('removeCoBuyer')}
                  >
                    <Minus size={22} strokeWidth={2.5} aria-hidden />
                  </button>
                  <h3 className="finance-apply-cobuyer-title">{pq('coBuyerSectionTitle')}</h3>
                </div>

                <div className="finance-apply-row2">
                  <div>
                    <label className="finance-apply-field-label" htmlFor="pq-cofn">
                      {pq('firstName')}
                    </label>
                    <input
                      id="pq-cofn"
                      className="finance-apply-input"
                      autoComplete="off"
                      placeholder={pq('firstName')}
                      value={form.coFirstName}
                      onChange={(e) => update('coFirstName', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="finance-apply-field-label" htmlFor="pq-coln">
                      {pq('lastName')}
                    </label>
                    <input
                      id="pq-coln"
                      className="finance-apply-input"
                      autoComplete="off"
                      placeholder={pq('lastName')}
                      value={form.coLastName}
                      onChange={(e) => update('coLastName', e.target.value)}
                    />
                  </div>
                </div>

                <label className="finance-apply-field-label" htmlFor="pq-codob">
                  {pq('dateOfBirth')}
                </label>
                <input
                  id="pq-codob"
                  type="date"
                  className="finance-apply-input"
                  value={form.coDob}
                  onChange={(e) => update('coDob', e.target.value)}
                />

                <label className="finance-apply-field-label" htmlFor="pq-coemail">
                  {pq('email')}
                </label>
                <input
                  id="pq-coemail"
                  type="email"
                  className="finance-apply-input"
                  autoComplete="off"
                  inputMode="email"
                  placeholder={pq('email')}
                  value={form.coEmail}
                  onChange={(e) => update('coEmail', e.target.value)}
                />

                <label className="finance-apply-field-label" htmlFor="pq-cophone">
                  {pq('phone')}
                </label>
                <input
                  id="pq-cophone"
                  type="tel"
                  className="finance-apply-input"
                  autoComplete="off"
                  inputMode="numeric"
                  placeholder={pq('phonePlaceholder')}
                  maxLength={14}
                  value={formatUsPhone(form.coPhone)}
                  onChange={(e) => update('coPhone', sanitizePhoneDigits(e.target.value))}
                />
                <p className="finance-apply-disclaimer">
                  {pq('phoneDisclaimerStart')}{' '}
                  <Link to="/terms-of-service" className="finance-apply-inline-link">
                    {pq('textingTerms')}
                  </Link>
                  .
                </p>

                <div className="finance-apply-ssn-field">
                  <label className="finance-apply-ssn-label" htmlFor="pq-cossn">
                    {pq('ssn')}
                  </label>
                  <div className="finance-apply-ssn-inner">
                    <Lock size={18} className="finance-apply-ssn-lock" aria-hidden />
                    <input
                      id="pq-cossn"
                      type="password"
                      className="finance-apply-input finance-apply-input--ssn"
                      autoComplete="off"
                      inputMode="numeric"
                      maxLength={11}
                      placeholder="•••-••-••••"
                      value={form.coSsn}
                      onChange={(e) => update('coSsn', e.target.value)}
                    />
                  </div>
                </div>
                <p className="finance-apply-disclaimer">
                  {pq('ssnDisclaimerStart')}{' '}
                  <Link to="/privacy-policy" className="finance-apply-inline-link">
                    {pq('privacyPolicy')}
                  </Link>
                  .
                </p>
              </div>
            )}
          </section>

          <section className="finance-apply-section-block finance-apply-section-block--divider" aria-labelledby="pq-section-housing">
            <h2 id="pq-section-housing" className="finance-apply-section-title">
              {pq('sectionHousing')}
            </h2>
            <label className="finance-apply-field-label" htmlFor="pq-street">
              {pq('streetAddress')}
            </label>
            <input
              id="pq-street"
              className="finance-apply-input"
              autoComplete="street-address"
              value={form.street}
              onChange={(e) => update('street', e.target.value)}
            />

            <label className="finance-apply-field-label" htmlFor="pq-apt">
              {pq('aptOptional')}
            </label>
            <input
              id="pq-apt"
              className="finance-apply-input"
              value={form.apt}
              onChange={(e) => update('apt', e.target.value)}
            />

            <label className="finance-apply-field-label" htmlFor="pq-city">
              {pq('city')}
            </label>
            <input
              id="pq-city"
              className="finance-apply-input"
              autoComplete="address-level2"
              value={form.city}
              onChange={(e) => update('city', e.target.value)}
            />

            <div className="finance-apply-row2">
              <div>
                <label className="finance-apply-field-label" htmlFor="pq-state">
                  {pq('state')}
                </label>
                <select
                  id="pq-state"
                  className="finance-apply-input finance-apply-select"
                  value={form.state}
                  onChange={(e) => update('state', e.target.value)}
                >
                  <option value="">{pq('selectState')}</option>
                  {US_STATES.map(([code, name]) => (
                    <option key={code} value={code}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="finance-apply-field-label" htmlFor="pq-zip">
                  {pq('zipCode')}
                </label>
                <input
                  id="pq-zip"
                  className="finance-apply-input"
                  inputMode="numeric"
                  autoComplete="postal-code"
                  placeholder={pq('zipPlaceholder')}
                  maxLength={10}
                  value={formatUsZip(form.zip)}
                  onChange={(e) => update('zip', sanitizeZipDigits(e.target.value))}
                />
              </div>
            </div>

            <div className="finance-apply-row2">
              <div>
                <label className="finance-apply-field-label" htmlFor="pq-housing">
                  {pq('housingStatus')}
                </label>
                <select
                  id="pq-housing"
                  className="finance-apply-input finance-apply-select"
                  value={form.housingStatus}
                  onChange={(e) => update('housingStatus', e.target.value)}
                >
                  <option value="">{pq('selectHousing')}</option>
                  <option value="rent">{pq('housingRent')}</option>
                  <option value="own">{pq('housingOwn')}</option>
                  <option value="family">{pq('housingFamily')}</option>
                  <option value="other">{pq('housingOther')}</option>
                </select>
              </div>
              <div>
                <label className="finance-apply-field-label" htmlFor="pq-movein">
                  {pq('moveInDate')}
                </label>
                <input
                  id="pq-movein"
                  type="month"
                  className="finance-apply-input"
                  value={form.moveInDate}
                  onChange={(e) => update('moveInDate', e.target.value)}
                />
              </div>
            </div>

            <label className="finance-apply-field-label" htmlFor="pq-monthly">
              {pq('monthlyHousing')}
            </label>
            <div className="finance-apply-currency-wrap">
              <DollarSign size={18} strokeWidth={2} className="finance-apply-currency-prefix" aria-hidden />
              <input
                id="pq-monthly"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                className="finance-apply-input finance-apply-input--currency finance-apply-input--money"
                placeholder="0.00"
                maxLength={11}
                value={formatMoneyIntDisplay(form.monthlyHousing)}
                onChange={(e) => update('monthlyHousing', normalizeMoneyIntInput(e.target.value, 7))}
              />
            </div>
          </section>

          <section className="finance-apply-section-block finance-apply-section-block--divider" aria-labelledby="pq-section-income">
            <h2 id="pq-section-income" className="finance-apply-section-title">
              {pq('sectionIncome')}
            </h2>

            <label className="finance-apply-field-label" htmlFor="pq-emp">
              {pq('employmentStatus')}
            </label>
            <select
              id="pq-emp"
              className="finance-apply-input finance-apply-select"
              value={form.employmentStatus}
              onChange={(e) => update('employmentStatus', e.target.value)}
            >
              <option value="">{pq('selectEmployment')}</option>
              <option value="employed">{pq('empEmployed')}</option>
              <option value="self">{pq('empSelf')}</option>
              <option value="unemployed">{pq('empUnemployed')}</option>
              <option value="retired">{pq('empRetired')}</option>
              <option value="student">{pq('empStudent')}</option>
            </select>

            {(form.employmentStatus === 'employed' || form.employmentStatus === 'self') ? (
              <>
                <label className="finance-apply-field-label" htmlFor="pq-employer">
                  {pq('employerName')}
                </label>
                <input
                  id="pq-employer"
                  type="text"
                  className="finance-apply-input"
                  autoComplete="organization"
                  placeholder={pq('employerNamePlaceholder')}
                  value={form.employerName}
                  onChange={(e) => update('employerName', e.target.value)}
                />
                <div className="finance-apply-row2">
                  <div>
                    <label className="finance-apply-field-label" htmlFor="pq-jobtitle">
                      {pq('jobTitle')}
                    </label>
                    <input
                      id="pq-jobtitle"
                      type="text"
                      className="finance-apply-input"
                      autoComplete="organization-title"
                      placeholder={pq('jobTitlePlaceholder')}
                      value={form.jobTitle}
                      onChange={(e) => update('jobTitle', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="finance-apply-field-label" htmlFor="pq-emp-start">
                      {pq('startDate')}
                      <span className="finance-apply-label-hint"> ({pq('startDateHint')})</span>
                    </label>
                    <input
                      id="pq-emp-start"
                      type="month"
                      className="finance-apply-input"
                      value={form.employmentStartDate}
                      onChange={(e) => update('employmentStartDate', e.target.value)}
                    />
                  </div>
                </div>
              </>
            ) : null}

            <div
              className="finance-apply-segmented"
              role="radiogroup"
              aria-label={pq('incomeTypeGroup')}
            >
              {[
                { value: 'salary', label: pq('incomeTypeSalary') },
                { value: 'hourly', label: pq('incomeTypeHourly') },
                { value: 'other', label: pq('incomeTypeOther') },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={form.incomeType === opt.value}
                  className={`finance-apply-segmented__btn${form.incomeType === opt.value ? ' is-active' : ''}`}
                  onClick={() => update('incomeType', opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {form.incomeType === 'hourly' ? (
              <div className="finance-apply-row2">
                <div>
                  <label className="finance-apply-field-label" htmlFor="pq-hourly-rate">
                    {pq('hourlyRateLabel')}
                  </label>
                  <div className="finance-apply-currency-wrap">
                    <DollarSign size={18} strokeWidth={2} className="finance-apply-currency-prefix" aria-hidden />
                    <input
                      id="pq-hourly-rate"
                      type="text"
                      inputMode="decimal"
                      autoComplete="off"
                      className="finance-apply-input finance-apply-input--currency finance-apply-input--money"
                      placeholder="0.00"
                      maxLength={12}
                      value={formatMoneyIntDisplay(form.hourlyRate)}
                      onChange={(e) => update('hourlyRate', normalizeMoneyIntInput(e.target.value, 9))}
                    />
                  </div>
                </div>
                <div>
                  <label className="finance-apply-field-label" htmlFor="pq-hours-week">
                    {pq('hoursPerWeekLabel')}
                  </label>
                  <div className="finance-apply-currency-wrap">
                    <DollarSign size={18} strokeWidth={2} className="finance-apply-currency-prefix" aria-hidden />
                    <input
                      id="pq-hours-week"
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      className="finance-apply-input finance-apply-input--currency finance-apply-input--money"
                      placeholder="0.00"
                      maxLength={3}
                      value={form.hoursPerWeek}
                      onChange={(e) => update('hoursPerWeek', normalizeHoursPerWeekInput(e.target.value))}
                    />
                  </div>
                </div>
              </div>
            ) : null}

            <div
              className="finance-apply-income-grid"
              role="group"
              aria-label={`${pq('annualIncomeLabel')}, ${pq('monthlyIncomeLabel')}`}
            >
              <div className="finance-apply-income-grid__col">
                <label className="finance-apply-field-label" htmlFor="pq-annual-inc">
                  {form.incomeType === 'hourly' ? pq('estimatedAnnualLabel') : pq('annualIncomeLabel')}
                </label>
                {form.incomeType === 'other' ? (
                  <div className="finance-apply-currency-wrap finance-apply-currency-wrap--full">
                    <DollarSign size={18} strokeWidth={2} className="finance-apply-currency-prefix" aria-hidden />
                    <input
                      id="pq-annual-inc"
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      className="finance-apply-input finance-apply-input--currency finance-apply-input--money"
                      placeholder="0.00"
                      maxLength={16}
                      value={formatMoneyIntDisplay(form.annualIncome)}
                      onChange={(e) => update('annualIncome', normalizeMoneyIntInput(e.target.value, 12))}
                    />
                  </div>
                ) : form.incomeType === 'salary' ? (
                  <div className="finance-apply-currency-wrap finance-apply-currency-wrap--full">
                    <DollarSign size={18} strokeWidth={2} className="finance-apply-currency-prefix" aria-hidden />
                    <input
                      id="pq-annual-inc"
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      className="finance-apply-input finance-apply-input--currency finance-apply-input--money"
                      placeholder="0.00"
                      maxLength={16}
                      value={formatMoneyIntDisplay(form.annualIncome)}
                      onChange={(e) => update('annualIncome', normalizeMoneyIntInput(e.target.value, 12))}
                    />
                  </div>
                ) : (
                  <div className="finance-apply-currency-wrap finance-apply-currency-wrap--full">
                    <DollarSign size={18} strokeWidth={2} className="finance-apply-currency-prefix" aria-hidden />
                    <input
                      id="pq-annual-inc"
                      type="text"
                      readOnly
                      tabIndex={-1}
                      className="finance-apply-input finance-apply-input--currency finance-apply-input--readonly-income finance-apply-input--money"
                      value={formatMoneyFigures(incomeResolved?.annual)}
                      aria-label={pq('estimatedAnnualLabel')}
                    />
                  </div>
                )}
              </div>
              <div className="finance-apply-income-grid__col">
                <label className="finance-apply-field-label" htmlFor="pq-monthly-inc">
                  {form.incomeType === 'hourly' ? pq('estimatedMonthlyLabel') : pq('monthlyIncomeLabel')}
                </label>
                {form.incomeType === 'other' ? (
                  <div className="finance-apply-currency-wrap finance-apply-currency-wrap--full">
                    <DollarSign size={18} strokeWidth={2} className="finance-apply-currency-prefix" aria-hidden />
                    <input
                      id="pq-monthly-inc"
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      className="finance-apply-input finance-apply-input--currency finance-apply-input--money"
                      placeholder="0.00"
                      maxLength={14}
                      value={formatMoneyIntDisplay(form.monthlyIncome)}
                      onChange={(e) => update('monthlyIncome', normalizeMoneyIntInput(e.target.value, 10))}
                    />
                  </div>
                ) : (
                  <div className="finance-apply-currency-wrap finance-apply-currency-wrap--full">
                    <DollarSign size={18} strokeWidth={2} className="finance-apply-currency-prefix" aria-hidden />
                    <input
                      id="pq-monthly-inc"
                      type="text"
                      readOnly
                      tabIndex={-1}
                      className="finance-apply-input finance-apply-input--currency finance-apply-input--readonly-income finance-apply-input--money"
                      value={formatMoneyFigures(incomeResolved?.monthly)}
                      aria-label={
                        form.incomeType === 'hourly'
                          ? pq('estimatedMonthlyLabel')
                          : pq('monthlyIncomeLabel')
                      }
                    />
                  </div>
                )}
              </div>
            </div>

            {!showAdditionalIncome ? (
              <button
                type="button"
                className="finance-apply-add-income-trigger"
                onClick={() => setShowAdditionalIncome(true)}
              >
                <span className="finance-apply-add-income-plus" aria-hidden>
                  <Plus size={22} strokeWidth={2.25} />
                </span>
                <span className="finance-apply-add-income-copy">
                  <span className="finance-apply-add-income-headline">{pq('addAdditionalIncome')}</span>
                  <span className="finance-apply-add-income-sub">{pq('additionalIncomeHint')}</span>
                </span>
              </button>
            ) : (
              <div className="finance-apply-additional-income">
                <label className="finance-apply-field-label" htmlFor="pq-addinc">
                  {pq('additionalIncomeLabel')}
                </label>
                <div className="finance-apply-currency-wrap">
                  <DollarSign size={18} strokeWidth={2} className="finance-apply-currency-prefix" aria-hidden />
                  <input
                    id="pq-addinc"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    className="finance-apply-input finance-apply-input--currency finance-apply-input--money"
                    placeholder="0.00"
                    maxLength={14}
                    value={formatMoneyIntDisplay(form.additionalIncome)}
                    onChange={(e) => update('additionalIncome', normalizeMoneyIntInput(e.target.value, 10))}
                  />
                </div>
                <p className="finance-apply-hint finance-apply-hint--additional-expanded">
                  {pq('additionalIncomeHint')}
                </p>
              </div>
            )}
            <p className="finance-apply-legal">{pq('incomeFootnote')}</p>
          </section>

          <section
            className="finance-apply-section finance-apply-terms-section"
            aria-labelledby="pq-terms-heading"
          >
            <h2 className="finance-apply-section-title" id="pq-terms-heading">
              {pq('termsSectionTitle')}
            </h2>
            <p className="finance-apply-terms-subtitle">{pq('termsSectionSubtitle')}</p>
            <div className="finance-apply-terms-scroll" tabIndex={0}>
              <PrequalTermsContent />
            </div>
            <label className="finance-apply-terms-agree" htmlFor="pq-terms-agree">
              <input
                id="pq-terms-agree"
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => {
                  setAgreedToTerms(e.target.checked);
                  if (error && e.target.checked) setError('');
                }}
              />
              <span>{pq('termsCheckbox')}</span>
            </label>
          </section>

          <div className="finance-apply-actions finance-apply-actions--single">
            <button
              type="submit"
              className="finance-apply-btn finance-apply-btn--primary finance-apply-btn--full"
              disabled={storesLoading || submitting || !agreedToTerms}
            >
              {submitting ? pq('submitting') : pq('submit')}
            </button>
          </div>
        </form>
      </main>
      <Footer />
    </div>
  );
}

export default FinanceApply;
